import { useState, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Progress } from "@/components/ui/progress";
import { Calendar, Users, MapPin, Clock, BookOpen, User, Info, CheckCircle, TrendingUp } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface LessonData {
  lesson_id: string;
  lesson_number: number;
  lesson_date: string | null;
  topic: string | null;
  location: string | null;
  time: string | null;
  group_name: string;
  lecturer_name: string;
  lecturer_id: string;
  lecturer_color: string;
}

interface AttendanceStats {
  present_count: number;
  total_students: number;
  attendance_percentage: number;
}

interface PhaseInfo {
  phase_name: string;
  phase_number: number;
}

interface LecturerProfile {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
}

interface DetailedLessonCardProps {
  lesson: LessonData;
}

export const DetailedLessonCard = ({ lesson }: DetailedLessonCardProps) => {
  const [attendanceStats, setAttendanceStats] = useState<AttendanceStats | null>(null);
  const [phaseInfo, setPhaseInfo] = useState<PhaseInfo | null>(null);
  const [lecturerProfile, setLecturerProfile] = useState<LecturerProfile | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchDetailedInfo = async () => {
    if (loading) return;
    
    setLoading(true);
    try {
      // Fetch attendance statistics
      const { data: attendanceData } = await supabase
        .from('attendance')
        .select('is_present')
        .eq('lesson_id', lesson.lesson_id);

      if (attendanceData) {
        const presentCount = attendanceData.filter(a => a.is_present).length;
        const totalStudents = attendanceData.length;
        const attendancePercentage = totalStudents > 0 ? (presentCount / totalStudents) * 100 : 0;
        
        setAttendanceStats({
          present_count: presentCount,
          total_students: totalStudents,
          attendance_percentage: attendancePercentage
        });
      }

      // Fetch phase information
      const { data: lessonDetails } = await supabase
        .from('lessons')
        .select('phase_id')
        .eq('id', lesson.lesson_id)
        .single();

      if (lessonDetails?.phase_id) {
        const { data: phaseData } = await supabase
          .from('phases')
          .select('name, phase_number')
          .eq('id', lessonDetails.phase_id)
          .single();

        if (phaseData) {
          setPhaseInfo({
            phase_name: phaseData.name,
            phase_number: phaseData.phase_number
          });
        }
      }

      // Fetch full lecturer profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('first_name, last_name, email, phone')
        .eq('id', lesson.lecturer_id)
        .single();

      if (profile) {
        setLecturerProfile(profile);
      }

    } catch (error) {
      console.error('Error fetching detailed info:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLessonStatus = () => {
    if (!lesson.lesson_date) return { status: 'unknown', icon: Info, color: 'gray' };
    
    const today = new Date();
    const lessonDate = new Date(lesson.lesson_date);
    
    if (lessonDate > today) {
      return { status: 'upcoming', icon: Calendar, color: 'blue' };
    } else {
      return { status: 'completed', icon: CheckCircle, color: 'green' };
    }
  };

  const lessonStatus = getLessonStatus();

  return (
    <HoverCard onOpenChange={(open) => open && fetchDetailedInfo()}>
      <HoverCardTrigger asChild>
        <Card className="bg-card border-border hover:border-border/70 transition-all duration-200 cursor-pointer hover:shadow-lg w-full min-w-0">
          <CardContent className="p-2 md:p-3 space-y-2">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-center gap-1 md:gap-2 min-w-0 flex-1">
                <Clock className="h-3 w-3 md:h-4 md:w-4 text-purple-400 flex-shrink-0" />
                <span className="text-foreground text-xs md:text-sm font-bold truncate">
                  {lesson.time || 'Ժամ նշված չէ'}
                </span>
                <Badge variant="outline" className="text-xs px-1 py-0 flex-shrink-0">
                  #{lesson.lesson_number}
                </Badge>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <lessonStatus.icon className={`h-3 w-3 text-${lessonStatus.color}-400`} />
                <Badge className={`${lesson.lecturer_color} text-foreground text-xs px-1 py-0 max-w-[80px] overflow-hidden`}>
                  <User className="h-3 w-3 mr-1 flex-shrink-0" />
                  <span className="truncate text-xs">
                    {lesson.lecturer_name.split(' ').map(n => n[0]).join('.')}
                  </span>
                </Badge>
              </div>
            </div>
            
            <div className="space-y-1 min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <Users className="h-3 w-3 text-blue-400 flex-shrink-0" />
                <span className="text-blue-300 text-xs truncate">{lesson.group_name}</span>
              </div>
              
              {lesson.topic && (
                <div className="flex items-center gap-2 min-w-0">
                  <BookOpen className="h-3 w-3 text-primary flex-shrink-0" />
                  <span className="text-muted-foreground text-xs truncate" title={lesson.topic}>
                    {lesson.topic}
                  </span>
                </div>
              )}
              
              {lesson.location && (
                <div className="flex items-center gap-2 min-w-0">
                  <MapPin className="h-3 w-3 text-yellow-400 flex-shrink-0" />
                  <span className="text-yellow-200 text-xs truncate" title={lesson.location}>
                    {lesson.location}
                  </span>
                </div>
              )}
              
              {lesson.lesson_date && (
                <div className="flex items-center gap-2 pt-1 border-t border-border min-w-0">
                  <Calendar className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                  <span className="text-muted-foreground text-xs truncate">
                    {new Date(lesson.lesson_date).toLocaleDateString('hy-AM')}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </HoverCardTrigger>
      
      <HoverCardContent className="w-80 max-w-[90vw] md:w-80 border-border/70 bg-card/80 text-muted-foreground" side="top">
        <div className="space-y-4">
          <div>
            <h4 className="text-muted-foreground font-semibold mb-2 flex items-center gap-2">
              <Info className="h-4 w-4" />
              Դասի մանրամասներ
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Դաս №:</span>
                <span className="text-foreground">{lesson.lesson_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Կարգավիճակ:</span>
                <Badge variant="outline" className={`text-${lessonStatus.color}-300 border-${lessonStatus.color}-400`}>
                  {lessonStatus.status === 'upcoming' ? 'Գալիք' : 'Անցած'}
                </Badge>
              </div>
            </div>
          </div>

          {lecturerProfile && (
            <div>
              <h4 className="text-muted-foreground font-semibold mb-2 flex items-center gap-2">
                <User className="h-4 w-4" />
                Դասախոս
              </h4>
              <div className="space-y-1 text-sm">
                <div className="text-foreground font-medium">
                  {lecturerProfile.first_name} {lecturerProfile.last_name}
                </div>
                <div className="text-muted-foreground">{lecturerProfile.email}</div>
                {lecturerProfile.phone && (
                  <div className="text-muted-foreground">{lecturerProfile.phone}</div>
                )}
              </div>
            </div>
          )}

          {phaseInfo && (
            <div>
              <h4 className="text-muted-foreground font-semibold mb-2 flex items-center gap-2">
                <BookOpen className="h-4 w-4" />
                Փուլ
              </h4>
              <div className="text-sm">
                <Badge variant="outline" className="text-purple-300 border-purple-400">
                  Փուլ {phaseInfo.phase_number}: {phaseInfo.phase_name}
                </Badge>
              </div>
            </div>
          )}

          {attendanceStats && (
            <div>
              <h4 className="text-muted-foreground font-semibold mb-2 flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Հաճախելիություն
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Ներկա:</span>
                  <span className="text-foreground">
                    {attendanceStats.present_count} / {attendanceStats.total_students}
                  </span>
                </div>
                <Progress 
                  value={attendanceStats.attendance_percentage} 
                  className="h-2"
                />
                <div className="text-xs text-center text-muted-foreground">
                  {attendanceStats.attendance_percentage.toFixed(1)}% ներկայություն
                </div>
              </div>
            </div>
          )}

          {loading && (
            <div className="text-center text-muted-foreground text-sm">
              Բեռնվում է...
            </div>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};